import { InfoIcon } from 'lucide-react';
import { FC, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { LanguageSelectorComboBox, TooltipLabel } from '@eluve/blocks';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  H2,
  HStack,
  InputLabel,
  P,
  VStack,
  textStyles,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentLanguages,
  useAppointmentStatus,
  useTenantId,
} from '@eluve/frontend-appointment-hooks';
import { useTenantUserSettings } from '@eluve/frontend-feature-user-settings';
import { SupportedLanguagesByCode } from '@eluve/language-utils';
import { useNamedLogger } from '@eluve/logger';
import { useDialog } from '@eluve/utility-hooks';

type AppointmentLanguageSelectorProps = {
  inline?: boolean;
  inputLanguage?: boolean;
  outputLanguage?: boolean;
  regenerateSummary?: boolean;
  onInputLanguageChange?: (newCode: string) => void;
};

/**
 * Allows the user to update the language of the appointment.
 *
 * @param inline determines if the selector should be displayed in an inline style
 * @param inputLanguage determines if the input language selector should be displayed
 * @param outputLanguage determines if the output language selector should be displayed
 * @param regenerateSummary determines if the summary should be regenerated after the output language is changed
 * @param onInputLanguageChange callback function to be called when the input language is changed
 */
export const AppointmentLanguageSelector: FC<
  AppointmentLanguageSelectorProps
> = ({
  inline = false,
  inputLanguage = false,
  outputLanguage = false,
  regenerateSummary = false,
  onInputLanguageChange,
}) => {
  const toast = useToast();
  const appointmentId = useAppointmentId();
  const tenantId = useTenantId();
  const { settings: userDefaultSettings } = useTenantUserSettings();
  const appointmentStatus = useAppointmentStatus();

  const logger = useNamedLogger(AppointmentLanguageSelector.name);
  const { isDialogOpen, toggleDialog } = useDialog();
  const apiClient = useApiClient();
  const [selectedOutputLanguage, setSelectedOutputLanguage] = useState<
    string | null
  >(null);

  const onOutputLanguageChange = regenerateSummary
    ? async (newCode: string) => {
        // Trigger summary regeneration
        try {
          const result = await apiClient.llm.summarizeAppointment({
            body: {},
            params: {
              tenantId,
              appointmentId,
            },
          });

          if (result.status !== 201) {
            toast.error('Failed to regenerate summary. Please try again.');
            logger.error('Failed to regenerate summary after language update', {
              tenantId,
              appointmentId,
              selectedLanguage: newCode,
            });
            return;
          }

          toast.success('Regenerating your summary in the new language', {
            description: 'Your new summary should be available soon',
          });
        } catch {
          toast.error('Failed to regenerate summary. Please try again.');
          logger.error(
            'Unexpected error when attempting to regenerate summary after language update',
            {
              tenantId,
              appointmentId,
              selectedLanguage: newCode,
            },
          );
        }
      }
    : undefined;

  const {
    inputLanguageCode,
    outputLanguageCode,
    isUserAppointmentOwner,
    preferredLanguages,
    updateInputLanguage,
    updateOutputLanguage,
    isUpdatingInputLanguage,
    isUpdatingOutputLanguage,
  } = useAppointmentLanguages({
    tenantId,
    appointmentId,
    userDefaultInputLanguage: userDefaultSettings?.inputLanguage,
    userDefaultOutputLanguage: userDefaultSettings?.outputLanguage,
    onInputLanguageChange,
    onOutputLanguageChange,
  });

  const handleNoteLanguageChange = async () => {
    if (!selectedOutputLanguage) {
      logger.error(
        'Output language update dialog open but no selected language',
        {
          tenantId,
          appointmentId,
        },
      );
      toast.error('Something went wrong. Please try again.');
      return;
    }

    try {
      toggleDialog();
      await updateOutputLanguage(selectedOutputLanguage);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      logger.error('Failed to update note language', {
        tenantId,
        appointmentId,
        selectedLanguage: selectedOutputLanguage,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setSelectedOutputLanguage(null);
    }
  };

  let inputTooltip;
  if (
    inputLanguage &&
    (appointmentStatus === 'ACTIVE' || appointmentStatus === 'COMPLETED')
  ) {
    inputTooltip = (
      <TooltipLabel
        label="Changing the spoken language will re-transcribe the entire conversation from the session's start."
        showArrow={true}
        tooltipArrowProps={{
          className: 'fill-white',
          height: 10,
          width: 20,
        }}
        tooltipContentProps={{
          align: 'start',
          alignOffset: -50,
          sideOffset: 8,
          className:
            'bg-white border-none text-text-secondary max-w-60 text-wrap shadow-md',
        }}
      >
        <InfoIcon size={16} />
      </TooltipLabel>
    );
  }

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={toggleDialog}>
        <DialogContent className="flex flex-col gap-5 p-8">
          <H2>Changing the notes language will generate a new note</H2>
          <VStack>
            <P>
              The new note will be generated in{' '}
              {selectedOutputLanguage
                ? SupportedLanguagesByCode[selectedOutputLanguage]
                : 'the selected language'}
              .
            </P>
            <P>This will overwrite the current summary.</P>
          </VStack>
          <DialogFooter className="w-full flex-row justify-end gap-2">
            <Button
              variant="ghost"
              className="w-fit bg-bg-quaternary hover:bg-border-primary"
              onClick={toggleDialog}
            >
              Cancel
            </Button>
            <Button onClick={handleNoteLanguageChange} className="w-fit">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VStack gap={5} className="items-start md:flex-row">
        {inputLanguage && (
          <Box fullWidth hStack={inline} vStack={!inline}>
            {inline ? (
              <HStack wFull={false}>
                {inputTooltip}
                <P className="text-nowrap font-semibold text-text-supporting">
                  Spoken Language:
                </P>
              </HStack>
            ) : (
              <HStack>
                <InputLabel label="Spoken Language" />
                {inputTooltip}
              </HStack>
            )}

            <LanguageSelectorComboBox
              ariaLabelPrefix="Spoken Language"
              selectButtonStyles={textStyles.body({
                weight: inline ? 'semibold' : 'regular',
                className: inline
                  ? 'text-text-supporting border-none text-nowrap'
                  : 'rounded-md border border-border-primary bg-bg-primary w-full px-3 py-2 text-sm text-text-secondary',
                size: 'md',
              })}
              disabled={!isUserAppointmentOwner || isUpdatingInputLanguage}
              selectedLanguageCode={inputLanguageCode}
              onSelectedLanguageCode={async (newCode) => {
                try {
                  await updateInputLanguage(newCode);
                } catch (error) {
                  toast.error('Something went wrong. Please try again.');
                  logger.error('Failed to update input language', {
                    tenantId,
                    appointmentId,
                    newCode,
                    error:
                      error instanceof Error ? error.message : String(error),
                  });
                }
              }}
              preferredLanguages={preferredLanguages}
              showLanguageIcon={false}
            />
          </Box>
        )}

        {outputLanguage && (
          <Box fullWidth hStack={inline} vStack={!inline}>
            {inline ? (
              <P className="text-nowrap font-semibold text-text-supporting">
                Notes Language:
              </P>
            ) : (
              <InputLabel label="Notes Language" />
            )}

            <LanguageSelectorComboBox
              ariaLabelPrefix="Notes Language"
              selectButtonStyles={textStyles.body({
                weight: inline ? 'semibold' : 'regular',
                className: inline
                  ? 'text-text-supporting border-none text-nowrap'
                  : 'rounded-md border border-border-primary bg-bg-primary w-full px-3 py-2 text-sm text-text-secondary',
                size: 'md',
              })}
              selectedLanguageCode={outputLanguageCode}
              disabled={!isUserAppointmentOwner || isUpdatingOutputLanguage}
              onSelectedLanguageCode={async (newCode) => {
                if (regenerateSummary) {
                  toggleDialog();
                  setSelectedOutputLanguage(newCode);
                } else {
                  try {
                    await updateOutputLanguage(newCode);
                  } catch (error) {
                    toast.error('Something went wrong. Please try again.');
                    logger.error('Failed to update output language', {
                      tenantId,
                      appointmentId,
                      newCode,
                      error:
                        error instanceof Error ? error.message : String(error),
                    });
                  }
                }
              }}
              preferredLanguages={preferredLanguages}
              showLanguageIcon={false}
            />
          </Box>
        )}
      </VStack>
    </>
  );
};
